import { useState } from "react";
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, getPasswordRequirementStatus } from "../../utils/password";

const EyeIcon = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const CheckIcon = ({ met }) =>
  met ? (
    <svg className="w-3.5 h-3.5 flex-shrink-0 text-statusGreen" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 flex-shrink-0 text-textMuted" fill="currentColor" viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="3" />
    </svg>
  );

/**
 * Generic password field: 8-14 chars, show/hide toggle, and an optional
 * live requirements checklist (uppercase/lowercase/number/special char).
 */
const PasswordInput = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  hint,
  required,
  disabled = false,
  autoComplete = "new-password",
  showRequirements = false,
  enforceMaxLength = true,
  minLength = PASSWORD_MIN_LENGTH,
  maxLength = PASSWORD_MAX_LENGTH,
}) => {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);

  const requirements = showRequirements ? getPasswordRequirementStatus(value || "") : [];
  const showChecklist = showRequirements && (focused || Boolean(value));

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-textPrimary mb-1.5">
          {label}
          {required && <span className="text-statusRed ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={name}
          name={name}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={(e) => { setFocused(false); onBlur?.(e); }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          minLength={minLength}
          maxLength={enforceMaxLength ? maxLength : undefined}
          className={`input-field pr-10 ${error ? "input-field-error" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-3 flex items-center text-textMuted hover:text-textPrimary transition-colors"
        >
          <EyeIcon open={visible} />
        </button>
      </div>

      {showChecklist && (
        <ul className="mt-2 space-y-1">
          {requirements.map((r) => (
            <li key={r.key} className={`flex items-center gap-1.5 text-xs ${r.met ? "text-statusGreen" : "text-textMuted"}`}>
              <CheckIcon met={r.met} />
              {r.label}
            </li>
          ))}
        </ul>
      )}

      {!showChecklist && hint && !error && <p className="mt-1 text-xs text-textMuted">{hint}</p>}
      {error && <p className="mt-1 text-xs text-statusRed">{error}</p>}
    </div>
  );
};

export default PasswordInput;
